import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { KeywordEntry } from '../../features/metrics/model/contracts'

interface WordCloudProps {
  keywords: KeywordEntry[]
}

/**
 * Minimalist word cloud — maps keyword frequency to font size.
 * Pure CSS with staggered entrance animation. No library needed.
 */
export const WordCloud = memo(function WordCloud({ keywords }: WordCloudProps) {
  const shuffled = useMemo(() => {
    const arr = [...keywords]
    let seed = 42
    for (let i = arr.length - 1; i > 0; i--) {
      seed = (seed * 16807 + 0) % 2147483647
      const j = seed % (i + 1)
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }, [keywords])

  if (keywords.length === 0) return null

  const maxVal = Math.max(...keywords.map((k) => k.value))
  const minVal = Math.min(...keywords.map((k) => k.value))
  const range = maxVal - minVal || 1

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-2">
      {shuffled.map((kw, i) => {
        const t = (kw.value - minVal) / range // 0..1
        const fontSize = 12 + t * 18 // 12px..30px
        const opacity = 0.45 + t * 0.55 // 0.45..1.0

        return (
          <span
            key={kw.text}
            className={cn(
              'inline-block cursor-default font-semibold leading-none transition-colors duration-200',
              'bg-clip-text text-transparent',
              'animate-[word-fade-in_0.4s_ease-out_both]',
            )}
            style={{
              backgroundImage:
                'linear-gradient(to right, var(--chart-line), var(--chart-line-alt))',
              fontSize: `${fontSize}px`,
              opacity,
              animationDelay: `${Math.min(i * 50, 400)}ms`,
            }}
          >
            {kw.text}
          </span>
        )
      })}
    </div>
  )
})
